<template>
	<div class="calendar__wrap">
		<div class="calendar__header">
			<div class="calendar__day">S</div>
			<div class="calendar__day">M</div>
			<div class="calendar__day">T</div>
			<div class="calendar__day">W</div>
			<div class="calendar__day">T</div>
			<div class="calendar__day">F</div>
			<div class="calendar__day">S</div>
		</div>
		<div class="calendar" :class="{ 'scroll-lock': scrollLock }" @scroll="onCalendarScroll" ref="calendar">
			<div
				class="scroll-buffer"
				v-for="i in scrollPage"
				:key="`scrollBuffer-${i}`"
				:style="{ height: `${numWeeksVisible * weekHeight}px` }"
			></div>
			<div class="calendar__month" :style="{ top: month.top }" v-for="month in months" :key="month.text">
				{{ month.text }}
			</div>
			<div class="calendar__week" v-for="week in weeks" :key="week[0].date.toString()">
				<div
					class="calendar__day"
					:class="dayClassObj(day)"
					v-for="day in week"
					@click="onDateClick($event, day)"
					:key="day.date.toString()"
				>
					<xyz-transition-group appear tag="div" class="day-bgs__wrap" xyz="fade small appear-stagger" :duration="300">
						<div
							class="day-bg xyz-none"
							:class="[`current-color--${selectedObj.color}`]"
							:style="dayBgStyleObj(selectedObjIndex, datesObj[day.dateKey].length)"
							v-for="(selectedObj, selectedObjIndex) in datesObj[day.dateKey]"
							:key="'day-bg-' + selectedObjIndex"
						>
							<shape class="day-bg-shape xyz-nested" :shape="selectedObj.shape" v-if="selectedObj.shape"></shape>
							<div class="day-bg-default xyz-nested" v-if="!selectedObj.shape"></div>
						</div>
					</xyz-transition-group>
					<div class="day-text">{{ day.date.day }}</div>
				</div>
			</div>
			<popover
				class="popover"
				name="calendarPopover"
				:ref-elem="clickedDate.elem"
				:options="popoverOptions"
				v-if="$scopedSlots.popover && clickedDate"
				:show="true"
				ref="popover"
			>
				<slot name="popover" :selected="clickedDate.selected"></slot>
			</popover>
		</div>
		<xyz-transition appear xyz="fade small">
			<button
				type="button"
				class="scroll-to-today button--help button--translucent translucent--white"
				v-if="scrollPage > 0"
				@click="scrollToTop"
			>
				<icon-arrow-up></icon-arrow-up>
			</button>
		</xyz-transition>
	</div>
</template>

<script>
import IconArrowUp from '@/components/icons/IconArrowUp.svg'
import Popover from '@/components/reusable/Popover'
import Shape from '@/components/reusable/Shape'
import * as utils from '@/utils'
import anime from 'animejs'
import { DateTime } from 'luxon'

export default {
	name: 'Calendar',
	props: {
		dates: {
			type: Array,
			required: true,
		},
		timeZone: {
			type: String,
		},
	},
	components: {
		IconArrowUp,
		Popover,
		Shape,
	},
	data() {
		return {
			currDate: null,
			weeks: [],
			months: [],
			firstDate: null,
			initialWeekBuffer: 2,
			numWeeksVisible: 0,
			scrollWeek: 0,
			weekHeight: 40,
			scrollLock: false,
			scrollLockTimeout: null,
			scrollLockDebounce: 200,
			popoverOptions: {
				modifiers: {
					preventOverflow: {
						priority: ['left', 'right'],
					},
				},
			},
			clickedDate: null,
		}
	},
	computed: {
		datesObj() {
			const datesObj = {}
			this.dates.forEach((selectedObj) => {
				const dateKey = utils.dateKey(selectedObj.date)
				if (!datesObj[dateKey]) {
					datesObj[dateKey] = []
				}
				datesObj[dateKey].push(selectedObj)
			})
			return datesObj
		},
		scrollPage() {
			if (this.numWeeksVisible) {
				return Math.floor(this.scrollWeek / this.numWeeksVisible)
			} else {
				return 0
			}
		},
	},
	watch: {
		timeZone() {
			this.initCalendar()
		},
		scrollPage() {
			this.updateCalendar()
		},
	},
	methods: {
		initCalendar() {
			if (this.timeZone) {
				this.currDate = DateTime.fromObject({}, { zone: this.timeZone }).startOf('day')
			} else {
				this.currDate = DateTime.local().startOf('day')
			}
			this.firstDate = this.currDate.startOf('week').minus({ weeks: this.initialWeekBuffer, days: 1 })
			if (this.dates.length) {
				const firstSelectedDate = this.dates[0]
				this.scrollToDate(firstSelectedDate.date, false)
			} else {
				this.updateCalendar()
			}
		},
		updateCalendar() {
			this.updateWeeks()
			this.updateMonths()
		},
		createWeek(firstDate) {
			const week = []
			for (let i = 0; i < 7; i++) {
				const date = firstDate.plus({ days: i })
				const dayDiff = Math.floor(date.diff(this.currDate, 'days').days)
				week.push({
					date,
					dateKey: utils.dateKey(date),
					old: dayDiff < 0,
					today: dayDiff === 0,
					shaded: date.month % 2,
				})
			}
			return week
		},
		updateWeeks() {
			this.weeks = []
			let firstDate = this.firstDate.plus({ weeks: this.scrollPage * this.numWeeksVisible })
			for (let i = 0; i < this.numWeeksVisible * 3; i++) {
				this.weeks.push(this.createWeek(firstDate))
				firstDate = firstDate.plus({ weeks: 1 })
			}
		},
		updateMonths() {
			this.months = []
			this.weeks.forEach((week, i) => {
				const lastDate = week[6].date
				if (lastDate.day >= 12 && lastDate.day < 19) {
					this.months.push({
						text: lastDate.plus({}).toFormat('LLLL yyyy'),
						top: `${(this.scrollPage * this.numWeeksVisible + i + 1) * this.weekHeight}px`,
					})
				}
			})
		},
		dayClassObj(day) {
			const classObj = {
				old: day.old,
				today: day.today,
				shaded: day.shaded,
			}
			const selectedArray = this.datesObj[day.dateKey]
			if (selectedArray) {
				if (selectedArray.length === 1) {
					classObj['selected'] = true
				} else if (selectedArray.length > 1) {
					classObj['selected-multi'] = true
				}
			}
			return classObj
		},
		dayBgStyleObj(index, numItems) {
			const styleObj = {
				transform: 'translate(-50%, -50%)',
			}
			if (numItems > 1) {
				const sectionRotation = 360 / 8
				const rotation = ((numItems - 1) / 2 - index) * sectionRotation
				styleObj['transform'] += `rotate(${rotation}deg) translateY(1rem)`
			}
			return styleObj
		},
		onCalendarScroll() {
			if (this.$refs.calendar) {
				const scrollTop = Math.max(0, this.$refs.calendar.scrollTop)
				this.scrollWeek = Math.floor(scrollTop / this.weekHeight)
			}
		},
		onWindowScroll() {
			this.scrollLock = true
			clearTimeout(this.scrollLockTimeout)
			this.scrollLockTimeout = setTimeout(() => {
				this.scrollLock = false
			}, this.scrollLockDebounce)
		},
		onWindowResize() {
			this.numWeeksVisible = Math.floor(this.$refs.calendar.clientHeight / this.weekHeight)
			this.onCalendarScroll()
		},
		scrollToTop() {
			anime({
				targets: this.$refs.calendar,
				scrollTop: 0,
				easing: 'easeOutQuad',
				duration: 500,
			})
		},
		scrollToDate(date, animate) {
			const weekNum = Math.max(
				0,
				Math.floor(date.diff(this.firstDate, 'weeks').weeks) - Math.floor(this.numWeeksVisible / 2) + 1
			)
			const scrollTop = weekNum * this.weekHeight
			if (animate) {
				anime({
					targets: this.$refs.calendar,
					scrollTop,
					easing: 'easeOutQuad',
					duration: 500,
				})
			} else {
				this.scrollWeek = weekNum
				this.updateCalendar()
				this.$nextTick(() => {
					this.$refs.calendar.scrollTop = scrollTop
				})
			}
		},
		onDateClick(event, day) {
			if (Math.floor(day.date.diff(this.currDate, 'days').days) >= 0) {
				const selected = this.datesObj[day.dateKey]
				if (selected) {
					this.clickedDate = {
						elem: event.currentTarget,
						selected,
					}
					event.stopPropagation()
				}
				this.$emit('date-click', day.date)
			}
		},
		onDocumentClick() {
			this.clickedDate = null
		},
	},
	mounted() {
		window.addEventListener('scroll', this.onWindowScroll)
		window.addEventListener('resize', this.onWindowResize)
		document.addEventListener('click', this.onDocumentClick)
		this.onWindowResize()
		this.initCalendar()
	},
	beforeDestroy() {
		window.removeEventListener('scroll', this.onWindowScroll)
		window.removeEventListener('resize', this.onWindowResize)
		document.removeEventListener('click', this.onDocumentClick)
		if (this.scrollLockTimeout) {
			clearTimeout(this.scrollLockTimeout)
		}
	},
}
</script>

<style lang="scss" scoped>
$weekHeight: 40px;
$monthBG: $gray50;

.calendar__wrap {
	position: relative;
	height: $weekHeight * 7;
	user-select: none;
	background-color: white;
	overflow: hidden;
}

.calendar__header {
	position: absolute;
	background-color: white;
	top: 0;
	left: 0;
	width: 100%;
	height: $weekHeight;
	display: flex;
	z-index: $z-forward-2;
	box-shadow: 0 0 10px 10px white;

	.day {
		font-weight: $fw-medium;
	}
}

.calendar {
	position: relative;
	height: 100%;
	overflow-y: auto;
	@include scroll-mobile;
	@include hide-scrollbar;
}

.scroll-lock {
	overflow-y: hidden;
}

.scroll-buffer {
	width: 100%;

	&:nth-child(even) {
		background-color: $monthBG;
	}
}

.calendar__month {
	position: absolute;
	width: 100%;
	opacity: 0.5;
	font-size: 1.125rem;
	font-weight: $fw-bold;
	text-transform: uppercase;
	text-align: center;
	transform: translateY(-50%);
	z-index: 1;
	pointer-events: none;
}

.calendar__week {
	position: relative;
	display: flex;
	height: $weekHeight;
}

.calendar__day {
	position: relative;
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: 0;
	font-size: $fs-small;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	background-color: white;

	&.shaded {
		background-color: $monthBG;
	}

	.day-text {
		position: relative;
	}

	.day-bgs__wrap {
		position: absolute;
		left: 50%;
		top: 50%;
	}

	.day-bg {
		position: absolute;
	}

	.day-bg-shape {
		height: 2rem;
	}

	.day-bg-default {
		@include circle(2rem);
		border-radius: 50%;
		background-color: theme-color(500);
	}

	&.old {
		cursor: default;

		.day-text {
			color: #aaa;
		}
	}

	&.today {
		&::before {
			content: '';
			position: absolute;
			left: 50%;
			top: 50%;
			@include circle(2rem);
			transform: translate(-50%, -50%);
			border: 2px solid theme-color(500);
		}

		.day-text {
			color: theme-color(500);
			font-weight: $fw-bold;
		}
	}

	&.selected {
		.day-text {
			color: white;
			font-weight: $fw-bold;
			font-size: 16px;
		}

		::v-deep {
			.icon-triangle {
				position: relative;
				top: -0.25rem;
			}
		}
	}

	&.selected-multi {
		.day-text {
			color: inherit;
		}

		.day-bg-shape {
			height: 0.6rem;
		}

		.day-bg-default {
			height: 0.6rem;
			width: 0.6rem;
		}
	}
}

.scroll-to-today {
	position: absolute;
	z-index: $z-forward-1;
	bottom: $spacing-xxs;
	right: $spacing-xxs;

	.icon-svg {
		width: 14px;
		height: auto;
	}
}

::v-deep {
	.popper {
		z-index: $z-forward-1;
	}
}
</style>
